/**
 * Sovg'a sheet — docs/SPEC.md §Sovg'a sheet.
 *
 * Tutqich → 3 tab (Ommabop · Lyuks · VIP) → 4 ustunli grid → pastki qator
 * (balans · kombo · Yuborish).
 *
 * **Klient narx yubormaydi** (qoida 4). Bu yerda narx faqat KO'RSATILADI,
 * so'rovga esa `gift_id` va `combo` ketadi.
 *
 * Katalog `GET /config` dan (D-063) — bu faylda birorta ham raqam yo'q.
 */

import type { GiftOut } from '@voice/api-client';
import { useMemo, useState } from 'react';

import { uz } from '../i18n/uz';
import { fmt } from '../lib/format';
import { CTAButton, Chip, Icon } from './index';
import './sheet.css';

const TIERS = ['popular', 'luxe', 'vip'] as const;
type Tier = (typeof TIERS)[number];

const TIER_LABEL: Record<Tier, string> = {
  popular: uz.gift.tierPopular,
  luxe: uz.gift.tierLuxe,
  vip: uz.gift.tierVip,
};

export function GiftSheet({
  gifts,
  balance,
  confirmThreshold,
  onSend,
  onClose,
}: {
  gifts: GiftOut[];
  balance: number;
  /** `limits.gift_confirm_threshold` — undan yuqorida tasdiq so'raladi (D-062). */
  confirmThreshold: number;
  onSend: (giftId: number, combo: 1 | 10 | 99) => void;
  onClose: () => void;
}) {
  const [tier, setTier] = useState<Tier>('popular');
  const [giftId, setGiftId] = useState<number | null>(null);
  const [combo, setCombo] = useState<1 | 10 | 99>(1);
  const [pendingConfirm, setPendingConfirm] = useState(false);

  const visible = useMemo(() => gifts.filter((g) => g.tier === tier), [gifts, tier]);
  const selected = visible.find((g) => g.id === giftId) ?? visible[0];
  const total = selected ? selected.price_gems * combo : 0;
  const affordable = total > 0 && total <= balance;

  function trySend(): void {
    if (!selected || !affordable) return;

    // D-062 — `x99` kombo bitta bosishda katta summani yechadi.
    // Tasdiqsiz bu chargeback va "ilova meni aldadi" shikoyatlarining
    // eng qisqa yo'li.
    if (total > confirmThreshold && !pendingConfirm) {
      setPendingConfirm(true);
      return;
    }
    onSend(selected.id, combo);
  }

  return (
    <>
      <div
        className="sheet-backdrop"
        role="presentation"
        onClick={onClose}
        data-testid="gift-backdrop"
      />
      <div className="sheet blur-sheet" role="dialog" aria-label={uz.gift.title}>
        <div className="sheet__handle" />

        <div className="sheet__tabs">
          {TIERS.map((value) => (
            <Chip
              key={value}
              /* `chip--sheet` — sheet ichidagi tab varianti (prototip 1028):
                 `flex:1`, markazda, 10px 0, 12.5/800. Baza Chip 9px 15px /
                 13px/700 — u qator ichida suzib, tablar teng bo'linmasdi. */
              className="chip--sheet"
              active={tier === value}
              onClick={() => {
                setTier(value);
                setGiftId(null);
                setPendingConfirm(false);
              }}
            >
              {TIER_LABEL[value]}
            </Chip>
          ))}
        </div>

        {visible.length === 0 ? (
          <div className="empty">{uz.gift.empty}</div>
        ) : (
          <div className="gift-grid">
            {visible.map((gift) => (
              <button
                key={gift.id}
                type="button"
                className="gift-cell"
                aria-pressed={selected?.id === gift.id}
                onClick={() => {
                  setGiftId(gift.id);
                  setPendingConfirm(false);
                }}
                title={gift.name}
              >
                <span className="gift-cell__icon">{gift.icon}</span>
                <span className="gift-cell__name">{gift.name}</span>
                <span className="gift-cell__price tnum">
                  {fmt(gift.price_gems)}
                  <Icon name="gem" size={8} />
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Pastki qator BITTA: balans · kombo · Yuborish (SPEC §2,
            prototip 1040-1051). Ilgari CTA alohida qatorda edi va sheet
            prototipdan 60px balandroq chiqardi. */}
        <div className="sheet__footer">
          {/* Prototipda (1041) bu pill bosilsa do'konga o'tadi. Bu yerda
              BOSILMAYDI: sheet qo'ng'iroq ustida ochiladi va do'konga
              o'tish jonli suhbatni uzib yuborardi. Balans yetmasa CTA
              matni buni aytadi. */}
          <div className="sheet__balance tnum">
            <Icon name="gem" size={13} />
            {fmt(balance)}
          </div>

          <div className="sheet__combos">
            {([1, 10, 99] as const).map((value) => (
              <Chip
                key={value}
                className="chip--combo"
                active={combo === value}
                onClick={() => {
                  setCombo(value);
                  setPendingConfirm(false);
                }}
              >
                {`×${value}`}
              </Chip>
            ))}
          </div>

          <CTAButton
            variant="gift"
            /* Sheet ichida CTA 46px (DESIGN_TOKENS §5, prototip 1050).
               58px to'liq ekran balandligi — sheet ichida u pastki
               qatorni ezib yuboradi. */
            size="sm"
            className="sheet__send"
            disabled={!selected || !affordable}
            /* Narx pill'i YO'Q — SPEC §2 va prototip 1050 da pastki qator
               "balans · kombo · Yuborish". Pill to'liq-en CTA davridan
               qolgan edi va bir qatorli tuzilishda tugmani toshirardi.
               Jami summa tasdiq bosqichida ko'rinadi (D-090). */
            onClick={trySend}
          >
            {/* Tasdiq bosqichi matnda ko'rinadi — alohida oyna emas.
                Sheet allaqachon modal, ustiga ikkinchi modal qo'yish
                kichkina ekranda chalkashtiradi. */}
            {!affordable && total > 0
              ? uz.gift.notEnough
              : pendingConfirm
                ? uz.gift.confirm(fmt(total))
                : uz.gift.send}
          </CTAButton>
        </div>
      </div>
    </>
  );
}
